// @flow
import { useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import moment from 'moment/dist/moment';

import withGroupClass from '@hoc/withGroupClass';
import { Activities } from '@activity/constants';
import BeneficiaryLayout from '@layout/components/BeneficiaryLayout';

import Button, { buttonConstants } from '@shared/Button';
import Modal from '@shared/Modal/Modal';
import ButtonGoBack from '@shared/Navigation/ButtonGoBack';

import AuthService, { type AuthServiceData } from '@user/services/AuthService';
import DateService from '_common/services/DateService';
import Toast from '_common/services/Toast/Toast';
import BeneficiaryGroupClassesRequestsService from '@beneficiary/services/BeneficiaryGroupClassesRequestsService';
import BeneficiaryGroupClassesRegisteredsService from '@beneficiary/services/BeneficiaryGroupClassesRegisteredsService';
import RequestsService from '@beneficiary/services/RequestsService';
import RolesHelpers from '@user/helpers/RolesHelpers';

import type { User } from '@user/types';
import type { ShortGroupClassBeneficiaryRequests } from '@participant/types';
import type { GroupClassDetailed, GroupClassRegistered } from '@groupClass/types';
import type { Coach } from '@coach/types';

import { LOADER_TYPE_PAGE } from '_common/components/loader/constants';
import { WEB_PATHS } from '@app/constants/paths';
import { GROUP_CLASS_REQUEST_INITIATED } from '@app/constants/constants';
import { CLASS_PAYMENT_STATUS_TO_PAY } from '@payment/constants';

import IconCalendar from '@icons/components/IconCalendar';
import IconMarkerMap from '@icons/components/IconMarkerMap';
import { FRANCE } from '@app/constants/constants';

const { GROUP_CLASSES_BENEFICIARY, BENEFICIARY_PAYMENT, PAGE_NOT_FOUND } = WEB_PATHS;

type Props = {
  groupClass: GroupClassDetailed | null,
};

type useParamsType = {
  classId: string,
};

export const GroupClassBeneficiary = (props: Props): React$Node => {
  const { groupClass } = props;

  const { classId } = useParams<useParamsType>();
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();

  const [ isLoadingRequest, setIsLoadingRequest ] = useState<boolean>(false);
  const [ groupClassesRequests, setGroupClassesRequests ] = useState<ShortGroupClassBeneficiaryRequests>([]);
  const [ isActiveModal, setIsActiveModal ] = useState<boolean>(false);
  const [ groupClassesRegistereds, setGroupClassesRegistereds ] = useState<GroupClassRegistered[]>([]);
  const [ loadedScope, setLoadedScope ] = useState<string | null>(null);
  const [ failedScope, setFailedScope ] = useState<string | null>(null);
  const [ loadAttempt, setLoadAttempt ] = useState<number>(0);
  const [ isCancelling, setIsCancelling ] = useState<boolean>(false);
  const [ currentUser, setCurrentUser ] = useState<User | null>(AuthService.user);

  const isBeneficiary = useMemo((): boolean => (
    RolesHelpers.isBeneficiary(currentUser)
  ), [currentUser]);

  const scope = `${ currentUser?.id || '' }:${ classId }:${ language }`;
  const isStateReady = loadedScope === scope && String(groupClass?.id) === classId;
  const activeRegistration = groupClassesRegistereds.find((registered) => (
    String(registered.groupClass.id) === classId && registered.unsubscribedAt === null
  ));
  const pendingRequest = groupClassesRequests.find((request) => (
    String(request.groupClass.id) === classId && request.status === GROUP_CLASS_REQUEST_INITIATED
  ));

  /**
   * toggleModal
   * Function used to toggle the visibility value of the modal
   * @param {void}
   * @returns {void}
   */
  const toggleModal = useCallback((): void => {
    setIsActiveModal(!isActiveModal);
  }, [isActiveModal]);

  /**
   * handleDeleteRequest
   * Function used to delete the request ID from the group class.
   * @param {void}
   * @returns {void}
   */
  const handleDeleteRequest = useCallback((): void => {
    if (!isStateReady || !pendingRequest || !currentUser || isCancelling) return;
    setIsCancelling(true);
    BeneficiaryGroupClassesRequestsService.deleteOne(pendingRequest.id, language, currentUser.id, {
      pagination: false,
      'groupClass.id': classId,
      status: GROUP_CLASS_REQUEST_INITIATED,
    })
      .then(() => {
        Toast.success(t('groupClasses.groupClass.peoples.preRegistrationCancelled'));
        navigate(GROUP_CLASSES_BENEFICIARY);
      })
      .catch(() => {
        Toast.error(t('groupClasses.groupClass.peoples.form.genericError'));
        setLoadAttempt((attempt) => attempt + 1);
      })
      .finally(() => setIsCancelling(false));
  }, [isStateReady, pendingRequest, currentUser, isCancelling, language, classId, t, navigate]);

  const handleDeleteRegistered = useCallback((): void => {
    if (!isStateReady || !activeRegistration || !currentUser || isCancelling) return;
    setIsCancelling(true);
    BeneficiaryGroupClassesRegisteredsService.deleteOne(activeRegistration.registered, language, currentUser.id, {
      pagination: false,
      'exists[unsubscribedAt]': false,
      'groupClass.id': classId,
    })
      .then(() => {
        Toast.success(t('groupClasses.groupClass.peoples.form.wellUnsubscribe'));
        navigate(GROUP_CLASSES_BENEFICIARY);
      })
      .catch(() => {
        Toast.error(t('groupClasses.groupClass.peoples.form.genericError'));
        setLoadAttempt((attempt) => attempt + 1);
      })
      .finally(() => setIsCancelling(false));
  }, [isStateReady, activeRegistration, currentUser, isCancelling, language, classId, t, navigate]);

  /**
   * handlePreregister
   * Function used to pre registered a beneficiary or a participant to the group class
   * @param {void}
   * @returns {void}
   */
  const handlePreregister = useCallback((): void => {
    if (isStateReady && !activeRegistration && !pendingRequest && !isLoadingRequest && isBeneficiary && groupClass && currentUser) {
      setIsLoadingRequest(true);
      RequestsService.preregisterBeneficiary(groupClass.id, currentUser.id)
        .then(() => {
          Toast.success(t('groupClasses.groupClass.peoples.preRegistrationDone'));
          setLoadedScope(null);
          setLoadAttempt((attempt) => attempt + 1);
        })
        .catch((error) => {
          if (error?.response?.status === 422) {
            Toast.error(t('groupClasses.preRegister.alreadyRequested'));
          } else {
            Toast.error(t('groupClasses.groupClass.peoples.form.genericError'));
          }
          setLoadedScope(null);
          setLoadAttempt((attempt) => attempt + 1);
        })
        .finally(() => setIsLoadingRequest(false));
    }
  }, [isStateReady, activeRegistration, pendingRequest, isLoadingRequest, currentUser, isBeneficiary, groupClass, t]);

  const handleUpdateAuthService = (data: AuthServiceData) => {
    setCurrentUser(data.user);
  };

  useEffect(() => AuthService.onChange(handleUpdateAuthService), []);

  // A singleton can contain another page's query. Only use results loaded for this scope.
  useEffect(() => {
    let isCurrent = true;
    setIsActiveModal(false);
    setLoadedScope(null);
    setFailedScope(null);
    if (isBeneficiary && currentUser?.id && classId) {
      Promise.all([
        BeneficiaryGroupClassesRegisteredsService.fetchAll(currentUser.id, language, {
          'exists[unsubscribedAt]': false,
          pagination: false,
          'groupClass.id': classId,
        }).then(() => BeneficiaryGroupClassesRegisteredsService.registeredsValues),
        BeneficiaryGroupClassesRequestsService.fetchAll(currentUser.id, language, {
          pagination: false,
          'groupClass.id': classId,
          status: GROUP_CLASS_REQUEST_INITIATED,
        }).then(() => BeneficiaryGroupClassesRequestsService.requestsValues),
      ])
        .then(([registereds, requests]) => {
          if (!isCurrent) return;
          setGroupClassesRegistereds(registereds);
          setGroupClassesRequests(requests);
          setLoadedScope(scope);
        })
        .catch(() => {
          if (!isCurrent) return;
          setFailedScope(scope);
          Toast.error(t('groupClasses.groupClass.peoples.form.genericError'));
        });
    }
    return () => { isCurrent = false; };
  }, [currentUser?.id, isBeneficiary, classId, language, scope, loadAttempt, t]);

  /**
   * useEffect used to go on not found page if the group class is closed
   */
  useEffect(() => {
    if (groupClass && groupClass?.seasonClosedAt) {
      navigate(PAGE_NOT_FOUND);
    }
  }, [groupClass]);

  const hasToPay = useMemo((): boolean => (
    groupClass && currentUser
      ? groupClassesRegistereds.some((element) => element.paymentStatus === CLASS_PAYMENT_STATUS_TO_PAY && element.groupClass.id === groupClass.id)
      : false
  ),[groupClassesRegistereds, currentUser, groupClass]);

  const footerModal: React$Node = useMemo((): React$Node => (
    <footer className="modal-footer">
      <Button
        type={ buttonConstants.TERTIARY }
        onClick={ toggleModal }
        isOutlined
      >
        { t('common.cancel') }
      </Button>

      <Button
        type={ buttonConstants.SECONDARY }
        className="is-success confirm"
        isLoading={ isCancelling }
        isDisabled={ !isStateReady }
        onClick={ activeRegistration ? handleDeleteRegistered : handleDeleteRequest }
      >
        { t('groupClasses.groupClass.peoples.form.popUpConfirm') }
      </Button>
    </footer>
  ), [toggleModal, handleDeleteRegistered, handleDeleteRequest, activeRegistration, isCancelling, isStateReady, t]);

  const isLoading = useMemo((): boolean => (
    !isStateReady && failedScope !== scope
  ), [isStateReady, failedScope, scope]);

  const startDate = useMemo((): string => (
    moment(groupClass?.startDate).locale(language).format('L')
  ), [groupClass, language]);

  const endDate = useMemo((): string => (
    moment(groupClass?.endDate).locale(language).format('L')
  ), [groupClass, language]);

  const startTime = useMemo((): string => (
    groupClass
      ? DateService.localDateAndTimeDisplay(groupClass.day, groupClass.startTime, groupClass.endTime, language)
      : ''
  ), [groupClass, language]);

  const addressGroupClass = useMemo((): string => (
    groupClass
      ? `${ groupClass.address.streetName } ${ groupClass.address.additionalLocalizationConstruction ?? '' } ${ groupClass.address.postalCode } ${ groupClass.address.city }`
      : ''
  ), [groupClass]);

  const mainCoach = useMemo((): Coach | null => (
    groupClass?.coaches.find((coach) => coach.isMainCoach) ?? null
  ), [groupClass]);

  const mainCoachName = useMemo((): string => (
    mainCoach?.sielBleuUser.firstName || ''
  ), [mainCoach]);

  const indicativePrice = groupClass?.activity.indicativePrice;

  const canPreregister = currentUser?.language.includes(groupClass?.sector.country.toLowerCase());

  return (
    <BeneficiaryLayout isLoading={ isLoading } loaderType={ LOADER_TYPE_PAGE }>
      <div className="group-class-card-container container">
        <div className="group-class-card-header">
          <div className="back-btn">
            <ButtonGoBack />
          </div>
          <div className="faq-toggle-btn">
            <a key={ t('user.faq') } target="_blank" rel="noreferrer" href={ t('user.faq.link') }>{ t('user.faq') }</a>
          </div>
        </div>

        { failedScope === scope && (
          <Button onClick={ () => setLoadAttempt((attempt) => attempt + 1) }>
            { t('groupClasses.registration.retry') }
          </Button>
        ) }

        { groupClass && (
          <>
            <div className="group-class-card">
              <div className="header">
                <div>
                  <h2 className="name">{ groupClass?.activity.name }</h2>
                  { mainCoach && (
                    <p className="coach">
                      { t('common.with') } <span><u>{ mainCoachName }</u></span>
                    </p>
                  ) }
                </div>
                { hasToPay && (
                  <div className="button-div">
                    <Link to={ BENEFICIARY_PAYMENT } className="button button-primary">
                      { t('payment.summary.payGroupClass') }
                    </Link>
                  </div>
                ) }
              </div>

              <div className="group-class-card-content">
                <div>
                  <div className="infos">
                    <div className="infos-datetime">
                      <IconCalendar />
                      <span>{ startTime }</span>
                    </div>
                    { !groupClass?.isOnline && (
                      <div className="infos-address">
                        <IconMarkerMap />
                        <span>{ addressGroupClass }</span>
                      </div>
                    ) }
                  </div>
                </div>

                <div className="group-class-card-content-block">
                  <h2 className="group-class-card-title">
                    { t('common.description') }
                  </h2>
                  <span>
                    { groupClass.description }
                  </span>
                </div>

                <div className="block-preregister-prices group-class-card-content-block">
                  { groupClass?.activity?.id !== Activities.GymVirtuelle && (
                    <div className="price-block">
                      <h2 className="group-class-card-title no-margin-top">
                        { t('groupClasses.groupClass.detailed.indicativePrices.title') }
                      </h2>
                      <span>{ t('groupClasses.groupClass.detailed.indicativePrices.price', { indicativePrice }) }</span>
                      { groupClass?.sector.country === FRANCE ? (
                        <ul className="price">
                          <li>{ t('groupClasses.groupClass.detailed.indicativePrices.firstLine') }</li>
                          <li>{ t('groupClasses.groupClass.detailed.indicativePrices.secondLine') }</li>
                          <li>{ t('groupClasses.groupClass.detailed.indicativePrices.thirdLine') }</li>
                        </ul>
                      ) : null }
                    </div>
                  ) }

                  { isStateReady && !pendingRequest && !activeRegistration && (
                    <div className="preregister-block">
                      <Button
                        className="first-time-preregister-button"
                        onClick={ handlePreregister }
                        type={ buttonConstants.PRIMARY }
                        disabled={ !canPreregister }
                        isLoading={ isLoadingRequest }
                      >
                        { t('groupClasses.groupClass.detailed.firstTimePreregisterButton') }
                      </Button>
                      <span className="first-time-preregister-text">
                        { t('groupClasses.groupClass.detailed.firstTimePreregister') }
                      </span>
                    </div>
                  ) }
                </div>

                <div className="group-class-card-content-block">
                  <h2 className="group-class-card-title">
                    { t('groupClasses.groupClass.detailed.season') }
                  </h2>
                  <span>
                    { t('dates.from') } { startDate }
                    &nbsp;
                    { t('dates.to') } { endDate }
                    <br />
                    { groupClass?.exceptDates && (
                      <span>{ t('groupClasses.groupClass.detailed.exceptDate') } { groupClass.exceptDates }</span>
                    ) }
                  </span>
                </div>

                { groupClass?.registrationTerms && (
                  <div className="group-class-card-content-block">
                    <h2 className="group-class-card-title">
                      { t('groupClasses.groupClass.detailed.registrationTerm') }
                    </h2>
                    <span>
                      { groupClass.registrationTerms }
                    </span>
                  </div>
                ) }

                { groupClass?.registrationContact && (
                  <div className="group-class-card-content-block">
                    <h2 className="group-class-card-title">
                      { t('groupClasses.groupClass.detailed.registrationContact') }
                    </h2>
                    <span>
                      { groupClass.registrationContact }
                    </span>
                  </div>
                ) }

                { (isStateReady && pendingRequest && !activeRegistration) && (
                  <div className="button-cancel-preregister">
                    <Button type={ buttonConstants.TERTIARY } isOutlined onClick={ toggleModal }>
                      { t('groupClasses.groupClass.detailed.cancelPreregister') }
                    </Button>
                  </div>
                ) }
                {
                  isStateReady && activeRegistration && (
                    <div className="button-cancel-preregister">
                      <Button type={ buttonConstants.TERTIARY } isOutlined onClick={ toggleModal }>
                        { t('groupClasses.groupClass.detailed.cancelRegister') }
                      </Button>
                    </div>
                  )
                }
              </div>
            </div>
            <Modal
              isActive={ isActiveModal }
              isClipped
              onClose={ toggleModal }
              title={
                activeRegistration
                  ? t('groupClasses.groupClass.detailed.cancelRegister')
                  : t('groupClasses.groupClass.detailed.cancelPreregister')
              }
              footer={ footerModal }
            >
              {
                activeRegistration
                  ? t('groupClasses.groupClass.detailed.cancelRegisterModal')
                  : t('groupClasses.groupClass.detailed.cancelPreregisterModal')
              }
            </Modal>
          </>
        ) }
      </div>
    </BeneficiaryLayout>
  );
};

export default withGroupClass(GroupClassBeneficiary);
